import { observable, toJS } from 'mobx';

import { FetchOptions, QueryCombiner, QueryObject, SerializedData, updateSearch } from 'bernie-core';
import { Store } from 'bernie-plugin-mobx';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import {
  cabinClassConverter,
  createSearchParameters,
  generateDeeplink,
  getPackageTypeFromSymbol,
  getTLAFromAirport,
  sanitizeFilterValues
} from 'utility';
import { compareFilterValues } from 'stores/validators/filter-validator';
import { getAddonsRequested, getSideSheetQueryContextFromSearchResults } from 'experiments/Air_Smart_Shopping_44809';
import { SearchQueryAdapter } from 'stores/adapters';
import { SearchFormStore, SortFilterStore } from '.';

import {
  AddOnRequested,
  DateInput,
  FlightAction,
  FlightsBoundNavigationFragment,
  FlightsCabinClass,
  FlightsDisplayComponentCountingValue,
  FlightsFaresSeparationType,
  FlightsFlexibleSearch,
  FlightsFlexSearchResults,
  FlightsJourneyCriteriaInput,
  FlightsNextJourneyCriteriaValues,
  FlightsQueryState,
  FlightsSearchContextInput,
  FlightsSearchFilterValuesInput,
  FlightsShoppingPwaFlightSearchFormLoaded,
  FlightsShoppingPwaFlightSearchResultsFlightsSearch,
  FlightsSortOptionTypeInput,
  FlightsSortOrder,
  FlightsSortType,
  FlightsStandardOffer,
  FlightsStopInfoInput,
  FlightsTravelerDetailsInput,
  FlightsTravelerType,
  FlightsTripType,
  MultiItemContextInput,
  RewardsOption,
  SideSheetQueryContextInput
} from '__generated__/api/types';
import TriggerFlightsSearchParams from 'types/shared/trigger-flights-search-params';
import NextSearchCriteria = FlightsFlexSearchResults.NextSearchCriteria;
import JourneySearchCriteria = FlightAction.JourneySearchCriteria;
import SearchPagination = FlightAction.SearchPagination;
import FlightsSearchFormType = FlightsShoppingPwaFlightSearchFormLoaded.FlightsLoadedSearchFormInlineFragment;
import FlightsSearchFormAirlineOption = FlightsShoppingPwaFlightSearchFormLoaded.FlightsSearchFormAirlineOptionInlineFragment;
import FlightsSearchFormCabinClassOption = FlightsShoppingPwaFlightSearchFormLoaded.FlightsSearchFormCabinClassOptionInlineFragment;
import FlightsSearchFormJourneyCriteriaType = FlightsShoppingPwaFlightSearchFormLoaded.NewJourneyTemplate;
import FlightsSearchFormMoreFiltersOption = FlightsShoppingPwaFlightSearchFormLoaded.FlightsSearchFormMoreFiltersOptionInlineFragment;
import FlightsSearchFormTravelersOption = FlightsShoppingPwaFlightSearchFormLoaded.FlightsSearchFormTravelersOptionInlineFragment;
import FlightsSearchFormTripTypeOptionItem = FlightsShoppingPwaFlightSearchFormLoaded._______Items;
import TravelerOptionItem = FlightsShoppingPwaFlightSearchFormLoaded.___Items;
import FlightsTravelerAgeSelector = FlightsShoppingPwaFlightSearchFormLoaded.TravelerAgeSelectorTemplate;
import FlightsFilterLoadedOptionType = FlightsShoppingPwaFlightSearchResultsFlightsSearch._Options;
import AppliedFiltersType = FlightsShoppingPwaFlightSearchResultsFlightsSearch.AppliedFilters;
import FlightsFilterItem = FlightsShoppingPwaFlightSearchResultsFlightsSearch.Items;
import FlightsLoadedListingResult = FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsLoadedListingResultInlineFragment;
import FlightsLoadedSortAndFilterResult = FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsLoadedSortAndFilterResultInlineFragment;

export class SearchCriteriaStore extends Store {
  @observable public searchCriteria: FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables;
  @observable public dialogSortAndFilter: {
    searchFilterValuesList: FlightsSearchFilterValuesInput | FlightsSearchFilterValuesInput[];
    sortOption: FlightsSortOptionTypeInput | null;
  };
  @observable public dialogSideSheet: {
    searchFilterValuesList: FlightsSearchFilterValuesInput | FlightsSearchFilterValuesInput[];
    addOnRequested: AddOnRequested[];
  };
  @observable public flexSearchCriteria: FlightsFlexibleSearch.Variables;
  @observable public searchFormCriteria: FlightsShoppingPwaFlightSearchFormLoaded.Variables;
  @observable public forceFetch: boolean;
  @observable public fetchFlexResults: boolean;
  @observable public fetchSearchForm: boolean;
  @observable public newSearch: boolean;
  @observable public reloadPageOnError: boolean;
  @observable public hasSmartShoppingCriteria: boolean;
  @observable public smartShoppingLoading: boolean;
  @observable public smartShoppingFirstLoad: boolean;
  @observable public isMetaExperiment = false;
  @observable public sideSheetQueryContext: SideSheetQueryContextInput;

  public rewardsOption: RewardsOption;
  public pageId: string;
  public multiItemPriceToken: string;
  public client?: string;
  public opaqueToken?: string;
  public itineraryNumber?: string;
  public servicingIntent: string;

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  public cleanUpSortFilterOption() {
    this.searchCriteria.sortOption = null;
    this.searchCriteria.searchFilterValuesList = [];
  }

  public updateFareCalendarPrice(price: string) {
    this.searchCriteria.fareCalendarPrice = price;
  }

  public updateFlightsDisplayComponentCountingValue(
    flightsDisplayComponentCountingValue: FlightsDisplayComponentCountingValue.Fragment
  ) {
    this.searchCriteria.flightsDisplayComponentValues = {
      displayCountingValues: []
    };
    this.searchCriteria.flightsDisplayComponentValues.displayCountingValues.push(flightsDisplayComponentCountingValue);
  }

  public setIsMetaExperiment(isMeta: boolean) {
    this.isMetaExperiment = isMeta;
  }

  private areFiltersCleanedUp(filters: string) {
    if (!filters) {
      if ((this.searchCriteria.searchFilterValuesList as FlightsSearchFilterValuesInput[]).length > 0) {
        this.searchCriteria.searchFilterValuesList = [];
        this.forceFetch = true;
      }

      return true;
    }

    return false;
  }

  private urlHasSameNumberOfFilters(filtersFromUrl: FlightsSearchFilterValuesInput[]) {
    return (
      filtersFromUrl.length === (this.searchCriteria.searchFilterValuesList as FlightsSearchFilterValuesInput[]).length
    );
  }

  public areSameFiltersApplied(filtersFromUrl: FlightsSearchFilterValuesInput[]) {
    let urlHasSameFilters = true;

    filtersFromUrl.forEach(filterInUrl => {
      const keyForFilterInUrl = Object.keys(filterInUrl)[0];
      const keyForFilterValueFromUrl = Object.keys(filterInUrl[keyForFilterInUrl])[0];
      const filterValueFromUrl = filterInUrl[keyForFilterInUrl][keyForFilterValueFromUrl];
      const filterFound = compareFilterValues(
        keyForFilterInUrl,
        keyForFilterValueFromUrl,
        filterValueFromUrl,
        this.searchCriteria.searchFilterValuesList
      );

      if (!filterFound) {
        urlHasSameFilters = false;
      }
    });

    return urlHasSameFilters;
  }

  public syncFiltersWithCriteriaFromUrl(filters: string): void {
    let filtersFromUrl = [];

    //TODO: Improve all this logic by having filter IDENTIFIERS https://jira.expedia.biz/browse/FODF-857

    if (this.areFiltersCleanedUp(filters)) {
      return;
    }

    try {
      filtersFromUrl = JSON.parse(filters);
    } catch (error) {
      this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'PARSE_FILTERS_FROM_URL'), error);

      return;
    }

    if (this.urlHasSameNumberOfFilters(filtersFromUrl) && this.areSameFiltersApplied(filtersFromUrl)) {
      return;
    }

    if (this.hasSmartShoppingCriteria) {
      this.sideSheetQueryContext.addOnRequested = getAddonsRequested(filtersFromUrl);
      this.dialogSideSheet.searchFilterValuesList = filtersFromUrl;
      this.dialogSideSheet.addOnRequested = getAddonsRequested(filtersFromUrl);
    }

    this.searchCriteria.searchFilterValuesList = filtersFromUrl;
    this.forceFetch = true;
  }

  private isSameSortOption(sortOptionInUrl: FlightsSortOptionTypeInput) {
    const sortOptionInCriteria = this.searchCriteria.sortOption;

    return (
      sortOptionInCriteria &&
      sortOptionInCriteria.sortOrder === sortOptionInUrl.sortOrder &&
      sortOptionInCriteria.sortType === sortOptionInUrl.sortType
    );
  }

  private isValidSortOption(sortOrder: string, sortType: string) {
    if (FlightsSortOrder[sortOrder] && FlightsSortType[sortType]) {
      return true;
    }

    return false;
  }

  public syncSortWithCriteriaFromUrl(sortOrder: string, sortType: string): boolean {
    if (!sortType || !sortOrder) {
      return;
    }

    if (!this.isValidSortOption(sortOrder, sortType)) {
      this.searchCriteria.sortOption = null;
      this.forceFetch = true;

      return;
    }

    const sortOptionInUrl: FlightsSortOptionTypeInput = {
      sortOrder: FlightsSortOrder[sortOrder],
      sortType: FlightsSortType[sortType]
    };

    if (this.isSameSortOption(sortOptionInUrl)) {
      return;
    }

    this.searchCriteria.sortOption = sortOptionInUrl;
    this.forceFetch = true;
  }

  private syncMultiItemIdWithUrl(misId?: string, queryPackageType?: string) {
    const packageType = getPackageTypeFromSymbol(queryPackageType);
    if (packageType && misId) {
      const shoppingContext = {
        multiItem: {
          id: misId,
          packageType: packageType
        }
      };
      this.searchCriteria.shoppingContext = shoppingContext;
      this.searchFormCriteria.shoppingContext = shoppingContext;
    }
  }

  public setCriteriaFromUrl(fetchOptions: FetchOptions) {
    const query = fetchOptions.query;
    const queryMap = new Map<string, string>();
    Object.keys(query).map(params => queryMap.set(params, query[params]));
    queryMap.delete('prevQuery');
    queryMap.delete('mode');
    queryMap.delete('0');
    const options = (query && query.options && query.options.split(',')) || [];
    queryMap.delete('options');
    const journey =
      query.trip === 'oneway'
        ? Object.keys(query).filter(name => name === 'leg1' && /leg/.test(name))
        : Object.keys(query).filter(name => /leg/.test(name));
    queryMap.delete('trip');
    journey.forEach(leg => queryMap.delete(leg));
    const passengers = (query && query.passengers && query.passengers.split(',')) || [];
    queryMap.delete('passengers');
    const localDateFormat = fetchOptions.request.context.localDateFormat;
    const locale = fetchOptions.request.context.locale;
    const journeysContinuationId = query && query.journeysContinuationId;
    queryMap.delete('journeysContinuationId');
    const originalBookingId = query && query.originalBookingId;
    queryMap.delete('originalBookingId');
    const hasCreditRedemptionIntent = query && query.hasCreditRedemptionIntent;
    queryMap.delete('hasCreditRedemptionIntent');
    this.pageId = (query && query.pageId) || 0;
    queryMap.delete('pageId');

    this.syncFiltersWithCriteriaFromUrl(query.filters);
    queryMap.delete('filters');

    this.syncSortWithCriteriaFromUrl(query.sortOrder, query.sortType);
    queryMap.delete('sortOrder');
    queryMap.delete('sortType');

    if (queryMap.get('client')) {
      this.client = queryMap.get('client');
    }
    if (queryMap.get('opaqueToken')) {
      this.opaqueToken = queryMap.get('opaqueToken');
    }
    if (queryMap.get('itineraryNumber')) {
      this.itineraryNumber = queryMap.get('itineraryNumber');
    }
    this.syncMultiItemIdWithUrl(query.misId, query.packageType);

    this.setJourneysContinuationId(journeysContinuationId);

    this.searchCriteria.flightsSearchContext.hasCreditRedemptionIntent = hasCreditRedemptionIntent ? true : null;

    this.searchFormCriteria.flightsSearchContext.hasCreditRedemptionIntent = hasCreditRedemptionIntent ? true : null;

    this.searchCriteria.flightsSearchContext.originalBookingId = originalBookingId || null;

    this.flexSearchCriteria.flightsSearchContext.originalBookingId = originalBookingId || null;

    this.searchFormCriteria.flightsSearchContext.originalBookingId = originalBookingId || null;

    this.searchCriteria.flightsSearchContext.tripType = SearchQueryAdapter.mapTripType(query.trip);

    this.flexSearchCriteria.flightsSearchContext.tripType = SearchQueryAdapter.mapTripType(query.trip);

    this.searchFormCriteria.flightsSearchContext.tripType = SearchQueryAdapter.mapTripType(query.trip);

    this.searchCriteria.searchPreferences.advancedFilters = SearchQueryAdapter.mapAdvancedFilters(options);

    this.flexSearchCriteria.searchPreferences.advancedFilters = SearchQueryAdapter.mapAdvancedFilters(options);

    this.searchFormCriteria.searchPreferences.advancedFilters = SearchQueryAdapter.mapAdvancedFilters(options);

    this.initVirtualAgentContextIfAvailable();

    this.searchCriteria.searchPreferences.airline = SearchQueryAdapter.mapAirline(
      options.find(option => option.includes('carrier'))
    );

    this.flexSearchCriteria.searchPreferences.airline = SearchQueryAdapter.mapAirline(
      options.find(option => option.includes('carrier'))
    );

    this.searchFormCriteria.searchPreferences.airline = SearchQueryAdapter.mapAirline(
      options.find(option => option.includes('carrier'))
    );

    this.searchCriteria.searchPreferences.cabinClass = SearchQueryAdapter.mapCabinClass(
      options.find(option => option.includes('cabinclass'))
    );

    this.flexSearchCriteria.searchPreferences.cabinClass = SearchQueryAdapter.mapCabinClass(
      options.find(option => option.includes('cabinclass'))
    );

    this.searchFormCriteria.searchPreferences.cabinClass = SearchQueryAdapter.mapCabinClass(
      options.find(option => option.includes('cabinclass'))
    );

    this.searchCriteria.travelerDetails = SearchQueryAdapter.mapTravelers(passengers);

    this.flexSearchCriteria.travelerDetails = SearchQueryAdapter.mapTravelers(passengers);

    this.searchFormCriteria.travelerDetails = SearchQueryAdapter.mapTravelers(passengers);

    this.searchCriteria.journeyCriteria = journey.map(leg =>
      SearchQueryAdapter.mapLeg(query[leg], locale, localDateFormat)
    );

    this.flexSearchCriteria.journeyCriteria = journey.map(leg =>
      SearchQueryAdapter.mapLeg(query[leg], locale, localDateFormat)
    );

    this.searchFormCriteria.journeyCriteria = journey.map(leg =>
      SearchQueryAdapter.mapLeg(query[leg], locale, localDateFormat)
    );

    if (this.isMetaExperiment) {
      this.searchCriteria.flightsSearchComponentCriteria.isMetaTraffic = this.isMetaExperiment;
      if (queryMap.has('paandi')) {
        for (const [key, value] of queryMap) {
          this.searchCriteria.flightsSearchComponentCriteria.queryParams.push({
            key: key,
            value: value?.toString()
          });
        }
      } else {
        this.searchCriteria.flightsSearchComponentCriteria.queryParams = [];
      }
    } else {
      this.searchCriteria.flightsSearchComponentCriteria = SearchQueryAdapter.mapQueryParams(queryMap);
    }
  }

  public triggerSearch({
    history,
    location,
    context,
    shouldForce,
    replaceHistory,
    fetchFlexResults = true,
    fetchSearchForm = true,
    removePageIdParam,
    reloadPageOnError,
    newSearchCriteria = null
  }: TriggerFlightsSearchParams) {
    const searchCriteria = newSearchCriteria ? newSearchCriteria : this.searchCriteria;

    const searchParameters = createSearchParameters(searchCriteria);
    const deeplink = generateDeeplink(
      searchParameters,
      context,
      this.pageId,
      removePageIdParam,
      this.multiItemPriceToken,
      this.rewardsOption,
      this.client,
      this.opaqueToken,
      this.itineraryNumber
    );
    const queryCombiner: QueryCombiner = (prevQuery, newQuery) => newQuery;

    if (shouldForce) {
      this.forceFetch = shouldForce;
    }
    this.reloadPageOnError = reloadPageOnError;
    this.fetchFlexResults = fetchFlexResults;
    this.fetchSearchForm = fetchSearchForm;

    setTimeout(() => {
      updateSearch({
        history,
        location,
        newParams: (deeplink as unknown) as QueryObject,
        replace: replaceHistory,
        queryCombiner
      });
    }, 10);
  }

  public syncWithNextSearchCriteria(nextSearchCriteria: NextSearchCriteria) {
    const { journeyCriteria, searchPreferences } = nextSearchCriteria as FlightsNextJourneyCriteriaValues.Fragment;
    const firstJourneyDepartureDate = journeyCriteria[0].departureDate;
    const newSearchCriteria = { ...toJS(this.searchCriteria) };

    if (this.hasSmartShoppingCriteria) {
      newSearchCriteria.journeyCriteria[0].departureDate = firstJourneyDepartureDate;
    }

    this.searchFormCriteria.journeyCriteria[0].departureDate = firstJourneyDepartureDate;
    this.flexSearchCriteria.journeyCriteria[0].departureDate = firstJourneyDepartureDate;

    if (journeyCriteria.length === 2) {
      const secondJourneyDepartureDate = journeyCriteria[1].departureDate;

      if (this.hasSmartShoppingCriteria && this.searchCriteria.journeyCriteria[1]) {
        newSearchCriteria.journeyCriteria[1].departureDate = secondJourneyDepartureDate;
      } else if (this.searchCriteria.journeyCriteria[1]) {
        this.searchCriteria.journeyCriteria[1].departureDate = secondJourneyDepartureDate;
      }

      if (this.searchFormCriteria.journeyCriteria[1]) {
        this.searchFormCriteria.journeyCriteria[1].departureDate = secondJourneyDepartureDate;
      }

      if (this.flexSearchCriteria.journeyCriteria[1]) {
        this.flexSearchCriteria.journeyCriteria[1].departureDate = secondJourneyDepartureDate;
      }
    }

    const cabinClass = cabinClassConverter(searchPreferences.cabinClass);

    if (this.hasSmartShoppingCriteria) {
      newSearchCriteria.searchPreferences.cabinClass = cabinClass;

      return newSearchCriteria;
    }

    this.searchCriteria.journeyCriteria[0].departureDate = firstJourneyDepartureDate;
    this.searchCriteria.searchPreferences.cabinClass = cabinClass;
    this.searchFormCriteria.searchPreferences.cabinClass = cabinClass;
    this.flexSearchCriteria.searchPreferences.cabinClass = cabinClass;

    return null;
  }

  public syncWithNextSearchCriteriaWithOD(nextSearchCriteria: JourneySearchCriteria) {
    const { journeyCriteria, searchPreferences } = nextSearchCriteria;

    for (let index = 0; index < journeyCriteria?.length; index++) {
      this.searchCriteria.journeyCriteria[index].departureDate = journeyCriteria[index].departureDate;
      this.searchCriteria.journeyCriteria[index].origin = journeyCriteria[index].origin;
      this.searchCriteria.journeyCriteria[index].destination = journeyCriteria[index].destination;

      this.searchFormCriteria.journeyCriteria[index].departureDate = journeyCriteria[index].departureDate;
      this.searchFormCriteria.journeyCriteria[index].origin = journeyCriteria[index].origin;
      this.searchFormCriteria.journeyCriteria[index].destination = journeyCriteria[index].destination;

      this.flexSearchCriteria.journeyCriteria[index].departureDate = journeyCriteria[index].departureDate;
      this.flexSearchCriteria.journeyCriteria[index].origin = journeyCriteria[index].origin;
      this.flexSearchCriteria.journeyCriteria[index].destination = journeyCriteria[index].destination;
    }

    this.searchCriteria.searchPreferences.cabinClass = searchPreferences.cabinClass;
    this.searchFormCriteria.searchPreferences.cabinClass = searchPreferences.cabinClass;
    this.flexSearchCriteria.searchPreferences.cabinClass = searchPreferences.cabinClass;
  }

  public setJourneysContinuationId(journeysContinuationId: string) {
    this.searchCriteria.flightsSearchContext.journeysContinuationId =
      (journeysContinuationId !== 'null' && journeysContinuationId) || null;
  }

  public setPreviousOriginalBookingId() {
    this.searchCriteria.flightsSearchContext.previousOriginalBookingId =
      this.searchCriteria.flightsSearchContext.originalBookingId || null;
  }

  public setOriginalBookingId(originalBookingId?: string) {
    this.setPreviousOriginalBookingId();
    this.searchCriteria.flightsSearchContext.originalBookingId = originalBookingId || null;
  }

  public syncWithSortAndFilterStore(sortAndFilterStore: SortFilterStore) {
    const searchCriteriaFeedback = Object.assign({}, this.searchCriteria);

    Object.assign(
      searchCriteriaFeedback,
      this.getSortOptionFromSortAndFilterResult(sortAndFilterStore.sortAndFilterResult)
    );
    const { filterPresentation } = sortAndFilterStore.sortAndFilterResult || {};
    Object.assign(
      searchCriteriaFeedback,
      filterPresentation &&
        filterPresentation.options &&
        this.getSearchFilterValuesListFromSortAndFilterResult(sortAndFilterStore)
    );
    Object.assign(searchCriteriaFeedback, this.unsetPagination());

    this.searchCriteria = searchCriteriaFeedback;
  }

  public syncWithAppliedSortAndFilterNextSearchCriteria(
    filterPill: AppliedFiltersType,
    sortOption: FlightsSortOptionTypeInput
  ) {
    const searchCriteriaFeedback = Object.assign({}, this.searchCriteria);
    const nextFilterCriteria = toJS(filterPill.nextSearchSortFilterCriteria.filterCriteria);
    const sanitizedNextFilterCriteria = sanitizeFilterValues(nextFilterCriteria);

    searchCriteriaFeedback.searchFilterValuesList = sanitizedNextFilterCriteria;
    searchCriteriaFeedback.sortOption = sortOption;
    this.searchCriteria = searchCriteriaFeedback;
  }

  private getPreviousFlightSelection(
    listingResult: FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsLoadedListingResultInlineFragment
  ) {
    const standardOfferListing = listingResult?.listings.find(
      listing => listing.__typename === 'FlightsStandardOffer'
    ) as FlightsStandardOffer.Fragment;

    if (!standardOfferListing) {
      return null;
    }

    const previousFlightSelection =
      standardOfferListing.journeys[0].flightsJourneyAvailableFaresInformation?.fareChoiceInformation?.fares[0]
        .selectFareAction.journeySearchCriteria?.previousFlightSelections;

    if (previousFlightSelection.length > 0) {
      return previousFlightSelection;
    }

    return null;
  }

  public syncWithSearchResults(
    searchResults: FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsSearch,
    searchForm: FlightsSearchFormType
  ) {
    const searchCriteria = this.getSearchCriteriaFromSearchResults(searchResults, searchForm);
    searchCriteria.queryState = FlightsQueryState.LOADED;
    searchCriteria.flightsSearchContext.journeysContinuationId =
      this.searchCriteria.flightsSearchContext.journeysContinuationId || null;
    this.forceFetch = false;
    searchCriteria.shoppingContext = searchResults.shoppingContext || null;
    this.searchFormCriteria.shoppingContext = searchResults.shoppingContext || null;
    if (this.hasSmartShoppingCriteria) {
      const sideSheetQueryContext = getSideSheetQueryContextFromSearchResults(
        searchCriteria.searchFilterValuesList as FlightsSearchFilterValuesInput[]
      );
      sideSheetQueryContext.journeyContinuationId = this.sideSheetQueryContext.journeyContinuationId || null;
      this.hydrate({ searchCriteria, sideSheetQueryContext });
    } else {
      this.hydrate({ searchCriteria });
    }

    const listingResult = searchResults.listingResult as FlightsLoadedListingResult;
    if (listingResult.listings.length > 0) {
      this.syncPreviousFlightSelections(this.getPreviousFlightSelection(listingResult));
    }
  }

  public syncWithSearchFormStore(searchFormStore: SearchFormStore, keepJCID = true, fromController = false) {
    const searchCriteriaFeedback = Object.assign({}, this.searchCriteria);
    const searchFormCriteriaFeedback = Object.assign({}, this.searchFormCriteria);
    const flexSearchCriteriaFeedback = Object.assign({}, this.flexSearchCriteria);

    const searchContextFromSearchForm = this.getFlightsSearchContextFromSearchForm(
      searchFormStore.searchForm,
      keepJCID
    );
    Object.assign(searchCriteriaFeedback, searchContextFromSearchForm);
    Object.assign(searchFormCriteriaFeedback, searchContextFromSearchForm);
    Object.assign(flexSearchCriteriaFeedback, searchContextFromSearchForm);

    const journeyCriteriaFromSearchForm = this.getJourneyCriteriaFromSearchForm(searchFormStore.searchForm);
    if (this.hasSmartShoppingCriteria && fromController) {
      Object.assign(searchCriteriaFeedback, this.searchCriteria.journeyCriteria);
    } else {
      Object.assign(searchCriteriaFeedback, journeyCriteriaFromSearchForm);
    }

    Object.assign(searchFormCriteriaFeedback, journeyCriteriaFromSearchForm);
    Object.assign(flexSearchCriteriaFeedback, journeyCriteriaFromSearchForm);

    const searchPreferencesFromSearchForm = this.getSearchPreferencesFromSearchForm(searchFormStore.searchForm);
    if (this.hasSmartShoppingCriteria && fromController) {
      Object.assign(searchCriteriaFeedback, this.searchCriteria.searchPreferences);
    } else {
      Object.assign(searchCriteriaFeedback, searchPreferencesFromSearchForm);
    }

    Object.assign(searchFormCriteriaFeedback, searchPreferencesFromSearchForm);
    Object.assign(flexSearchCriteriaFeedback, searchPreferencesFromSearchForm);

    const travelerDetailsFromSearchForm = this.getTravelerDetailsFromSearchForm(searchFormStore.searchForm);
    Object.assign(searchCriteriaFeedback, travelerDetailsFromSearchForm);
    Object.assign(searchFormCriteriaFeedback, travelerDetailsFromSearchForm);
    Object.assign(flexSearchCriteriaFeedback, travelerDetailsFromSearchForm);

    Object.assign(searchCriteriaFeedback, this.unsetPagination());

    this.searchCriteria = searchCriteriaFeedback;
    this.searchFormCriteria = searchFormCriteriaFeedback;
    this.flexSearchCriteria = flexSearchCriteriaFeedback;
  }

  public updateSearchCriteriaWithPagination(searchPagination?: SearchPagination) {
    const searchCriteriaFeedback = Object.assign({}, this.searchCriteria);

    Object.assign(searchCriteriaFeedback, {
      searchPagination: this.searchPaginationToSearchPaginationInput(searchPagination)
    });

    this.searchCriteria = searchCriteriaFeedback;
  }

  public updateSearchCriteriaWithMultiItemContext(multiItemContext: MultiItemContextInput) {
    if (multiItemContext) {
      const shoppingContext = {
        multiItem: multiItemContext
      };
      this.searchCriteria.shoppingContext = shoppingContext;
      this.searchFormCriteria.shoppingContext = shoppingContext;
    }
  }

  public updateSearchCriteriaWithFaresSeparation(faresSeparationType?: FlightsFaresSeparationType) {
    const searchCriteriaFeedback = Object.assign({}, this.searchCriteria);
    Object.assign(searchCriteriaFeedback, {
      faresSeparationType: faresSeparationType
    });

    this.searchCriteria = searchCriteriaFeedback;
  }

  public initVirtualAgentContextIfAvailable() {
    this.searchCriteria.virtualAgentContext = SearchQueryAdapter.mapVirtualAgentContext(
      this.opaqueToken,
      this.itineraryNumber
    );
  }

  public updateSearchCriteriaWithTravelerDetails(travelerDetails: FlightsTravelerDetailsInput[]) {
    const mappedDetails = travelerDetails.map(({ travelerType, ages, count }) => ({
      travelerType,
      ages,
      count
    }));
    this.searchCriteria.travelerDetails = mappedDetails;
    this.flexSearchCriteria.travelerDetails = mappedDetails;
    this.searchFormCriteria.travelerDetails = mappedDetails;
  }

  public updateSearchCriteriaWithFlightsSearchContext(flightsSearchContext: FlightsSearchContextInput) {
    const mappedFlightSearchContext: FlightsSearchContextInput = {
      tripType: flightsSearchContext.tripType,
      journeysContinuationId: flightsSearchContext.journeysContinuationId,
      originalBookingId: flightsSearchContext.originalBookingId,
      previousFlightSelections: flightsSearchContext.previousFlightSelections,
      previousOriginalBookingId: flightsSearchContext.previousOriginalBookingId,
      searchCriteriaMismatch: flightsSearchContext.searchCriteriaMismatch,
      hasCreditRedemptionIntent: flightsSearchContext.hasCreditRedemptionIntent
    };
    this.searchCriteria.flightsSearchContext = mappedFlightSearchContext;
    this.searchFormCriteria.flightsSearchContext = mappedFlightSearchContext;
    this.flexSearchCriteria.flightsSearchContext = mappedFlightSearchContext;
  }

  public syncPreviousFlightSelections(previousFlightSelections: FlightAction.PreviousFlightSelections[]) {
    if (previousFlightSelections) {
      this.searchCriteria.flightsSearchContext.previousFlightSelections = previousFlightSelections;
    }
  }

  public getSearchCriteriaFromSearchResults(
    searchResults: FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsSearch,
    searchForm: FlightsSearchFormType
  ) {
    const searchCriteriaFeedback = Object.assign(
      {},
      SearchCriteriaStore.DEFAULTS.searchCriteria
    ) as FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables;

    if (searchForm) {
      Object.assign(searchCriteriaFeedback, this.getFlightsSearchContextFromSearchForm(searchForm));

      if (this.hasSmartShoppingCriteria) {
        Object.assign(searchCriteriaFeedback, { journeyCriteria: this.searchCriteria.journeyCriteria });
        Object.assign(searchCriteriaFeedback, { searchPreferences: this.searchCriteria.searchPreferences });
      } else {
        Object.assign(searchCriteriaFeedback, this.getJourneyCriteriaFromSearchForm(searchForm));
        Object.assign(searchCriteriaFeedback, this.getSearchPreferencesFromSearchForm(searchForm));
      }

      Object.assign(searchCriteriaFeedback, this.getTravelerDetailsFromSearchForm(searchForm));
    }
    if (!searchCriteriaFeedback.journeyCriteria[0].departureDate.day) {
      Object.assign(searchCriteriaFeedback, {
        journeyCriteria: this.searchCriteria.journeyCriteria
      });
    }

    const sortAndFilterResult = searchResults.sortAndFilterResult as FlightsLoadedSortAndFilterResult;
    const filtersFromSpinner = this.getFiltersAppliedFromSpinnerResponse(sortAndFilterResult);

    if (
      this.urlHasSameNumberOfFilters(filtersFromSpinner.searchFilterValuesList) &&
      this.areSameFiltersApplied(filtersFromSpinner.searchFilterValuesList)
    ) {
      Object.assign(searchCriteriaFeedback, {
        searchFilterValuesList: toJS(this.searchCriteria.searchFilterValuesList)
      });
    } else {
      Object.assign(searchCriteriaFeedback, filtersFromSpinner);
    }

    Object.assign(searchCriteriaFeedback, this.getSortOptionFromSortAndFilterResult(sortAndFilterResult));

    return searchCriteriaFeedback;
  }

  public getFlightsSearchContextFromSearchForm(
    searchForm: FlightsSearchFormType,
    keepJCID = true
  ): {
    flightsSearchContext: FlightsSearchContextInput;
  } {
    const items = searchForm.searchOptions.find(
      searchOption => searchOption.__typename === 'FlightsSearchFormTripTypeOption'
    ).items as FlightsSearchFormTripTypeOptionItem[];
    const { tripType } = items.find(tripTypeOption => tripTypeOption.selected);
    const journeyContinuationId =
      (keepJCID && this.searchCriteria?.flightsSearchContext?.journeysContinuationId) || null;
    const originalBookingId = this.searchCriteria?.flightsSearchContext.originalBookingId || null;
    const previousOriginalBookingId = this.searchCriteria?.flightsSearchContext.previousOriginalBookingId || null;
    const hasCreditRedemptionIntent = this.searchCriteria?.flightsSearchContext.hasCreditRedemptionIntent ?? null;

    return {
      flightsSearchContext: {
        tripType,
        journeysContinuationId: journeyContinuationId,
        originalBookingId,
        hasCreditRedemptionIntent,
        previousOriginalBookingId
      }
    };
  }

  public getSelectedTripType(searchForm: FlightsSearchFormType) {
    const items = searchForm.searchOptions.find(
      searchOption => searchOption.__typename === 'FlightsSearchFormTripTypeOption'
    ).items as FlightsSearchFormTripTypeOptionItem[];
    const { tripType } = items.find(tripTypeOption => tripTypeOption.selected);

    return tripType;
  }

  public getJourneyCriteriaFromSearchForm(searchForm: FlightsSearchFormType) {
    const tripType: string = this.getSelectedTripType(searchForm);
    let journeyCriteria = [];

    searchForm.journeys.forEach(journey => {
      journeyCriteria = this.mapSearchFormJourneyToInputJourney(journeyCriteria, journey, tripType);
    });

    return {
      journeyCriteria
    };
  }

  public mapSearchFormJourneyToInputJourney(
    journeyCriteria: FlightsJourneyCriteriaInput[],
    flightsSearchFormJourneyCriteria: FlightsSearchFormJourneyCriteriaType,
    tripType: string
  ): FlightsJourneyCriteriaInput[] {
    const departureDate = flightsSearchFormJourneyCriteria.departureDate.date;
    const returnDate = flightsSearchFormJourneyCriteria.returnDate && flightsSearchFormJourneyCriteria.returnDate.date;
    const { originLocation, destinationLocation } = flightsSearchFormJourneyCriteria;
    const inputJourney = {
      departureDate: {
        day: departureDate.day,
        month: departureDate.month,
        year: departureDate.year
      },
      destination: destinationLocation.value,
      origin: originLocation.value
    };

    journeyCriteria.push(inputJourney);
    if (tripType === 'ROUND_TRIP' && returnDate) {
      journeyCriteria.push({
        departureDate: {
          day: returnDate.day,
          month: returnDate.month,
          year: returnDate.year
        },
        destination: originLocation.value,
        origin: destinationLocation.value
      });
    }

    return journeyCriteria;
  }

  public getFiltersAppliedFromSpinnerResponse(
    sortAndFilterResult: FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsLoadedSortAndFilterResultInlineFragment
  ) {
    const filtersPresentation = sortAndFilterResult.filterPresentation;

    if (!filtersPresentation || !filtersPresentation.options) {
      return {
        searchFilterValuesList: []
      };
    }

    const searchFilterValuesList = this.flattenFilterValueInputsFromFilterOptions(
      filtersPresentation.options.map(this.getFilterValuesInputFromFilterOption.bind(this))
    );

    return { searchFilterValuesList };
  }

  public getSearchFilterValuesListFromSortAndFilterResult(sortAndFilterResult: SortFilterStore) {
    if (sortAndFilterResult.sortAndFilterResult?.filterPresentation.__typename === 'FlightsFilterLoadingPresentation') {
      return;
    }

    const searchFilterValuesList = this.flattenFilterValueInputsFromFilterOptions(
      sortAndFilterResult.sortAndFilterResult?.filterPresentation.options.map(
        this.getFilterValuesInputFromFilterOption.bind(this)
      )
    );

    return { searchFilterValuesList };
  }

  public getFilterValuesInputFromFilterOption(
    filterOption: FlightsFilterLoadedOptionType
  ): FlightsSearchFilterValuesInput[] {
    let flightsSearchFilterValues = [];

    if (filterOption.__typename !== 'FlightsSliderDisplayFilterOption') {
      flightsSearchFilterValues = (filterOption.items as FlightsFilterItem[])
        .filter(filterItem => filterItem.selected)
        .map(filterItem => filterItem.flightsSearchFilterValues);
    }

    return sanitizeFilterValues(flightsSearchFilterValues);
  }

  public flattenFilterValueInputsFromFilterOptions(filterValuesFromFilterOptions) {
    return filterValuesFromFilterOptions.reduce((filterValues, filterOptionInputs) => {
      filterOptionInputs.forEach(filterOptionInput => filterValues.push(filterOptionInput));

      return filterValues;
    }, []);
  }

  public getSearchPreferencesFromSearchForm(searchForm: FlightsSearchFormType) {
    const moreFiltersOption = searchForm.searchOptions.find(
      searchOption => searchOption.__typename === 'FlightsSearchFormMoreFiltersOption'
    ) as FlightsSearchFormMoreFiltersOption;
    const advancedFilters = moreFiltersOption?.items
      ?.filter(moreFilterOption => moreFilterOption.selected)
      .map(moreFilterOption => moreFilterOption.advancedFilterType);

    const airlineOption = searchForm.searchOptions.find(
      searchOption => searchOption.__typename === 'FlightsSearchFormAirlineOption'
    ) as FlightsSearchFormAirlineOption;
    const airline = airlineOption?.selectedItem.airline;

    const cabinClassOption = searchForm.searchOptions.find(
      searchOption => searchOption.__typename === 'FlightsSearchFormCabinClassOption'
    ) as FlightsSearchFormCabinClassOption;
    const cabinClass = cabinClassOption?.selectedItem.cabinClass;

    return {
      searchPreferences: {
        advancedFilters,
        airline,
        cabinClass
      }
    };
  }

  public getSortOptionFromSortAndFilterResult(
    sortAndFilterResult:
      | FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsLoadedSortAndFilterResultInlineFragment
      | SortFilterStore['sortAndFilterResult']
  ) {
    return {
      sortOption: {
        ...sortAndFilterResult.sortPresentation.selectedOption.sortType
      }
    };
  }

  public unsetPagination() {
    return {
      searchPagination: null
    };
  }

  public searchPaginationToSearchPaginationInput(searchPagination?: SearchPagination) {
    return searchPagination
      ? {
          size: searchPagination.size,
          startingIndex: searchPagination.startingIndex
        }
      : null;
  }

  public getTravelerDetailsFromSearchForm(searchForm: FlightsSearchFormType) {
    const travelerItems = (searchForm.searchOptions.find(
      searchOption => searchOption.__typename === 'FlightsSearchFormTravelersOption'
    ) as FlightsSearchFormTravelersOption).items;
    const travelerDetails = travelerItems
      ?.filter(item => item.travelerCount !== 0)
      .map(this.travelerOptionToTravelerDetailsInput.bind(this));

    return {
      travelerDetails
    };
  }

  public travelerOptionToTravelerDetailsInput(travelerOptionItem: TravelerOptionItem) {
    return {
      ages: travelerOptionItem.travelerAges && this.getTravelerAgesFromSelectors(travelerOptionItem.travelerAges),
      count: travelerOptionItem.travelerCount,
      travelerType: travelerOptionItem.type
    };
  }

  public getTravelerAgesFromSelectors(travelerAges: FlightsTravelerAgeSelector[]) {
    return travelerAges.reduce((ages, travelerAgeSelector) => {
      ages.push(travelerAgeSelector.items.find(item => item.selected).value);

      return ages;
    }, []);
  }

  public clearSortFilterSearchCriteria(
    newSearchCriteria?: FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables
  ): void {
    if (newSearchCriteria) {
      newSearchCriteria.searchFilterValuesList = [];
      newSearchCriteria.sortOption = null;

      return;
    }

    this.searchCriteria.searchFilterValuesList = [];
    this.searchCriteria.sortOption = null;
  }

  public clearJourneysContinuationId(
    newSearchCriteria?: FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables
  ): void {
    if (newSearchCriteria) {
      newSearchCriteria.flightsSearchContext.journeysContinuationId = null;

      return;
    }

    delete this.searchCriteria.flightsSearchContext.journeysContinuationId;
  }

  public clearPagination(newSearchCriteria?: FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables): void {
    if (newSearchCriteria) {
      newSearchCriteria.searchPagination = null;
      newSearchCriteria.faresSeparationType = FlightsFaresSeparationType.BASE_AND_UPSELL;

      return;
    }
    this.searchCriteria.searchPagination = null;
    this.searchCriteria.faresSeparationType = FlightsFaresSeparationType.BASE_AND_UPSELL;
  }

  public frameJourneyCriterias(pageId: number, regionId?: string) {
    const journeyCriterias = [];
    const journeyCriteria = this.searchCriteria?.journeyCriteria as FlightsJourneyCriteriaInput[];

    let startDate = '';
    let endDate = '';
    if (journeyCriteria?.length) {
      startDate = SearchCriteriaStore.stitchDate(journeyCriteria[0].departureDate);
      endDate = SearchCriteriaStore.stitchDate(journeyCriteria[journeyCriteria.length - 1].departureDate);
    }

    const tripType = this.searchCriteria?.flightsSearchContext?.tripType;
    const cabinClass = this.searchCriteria?.searchPreferences?.cabinClass;
    const travelerDetail = this.frameTravelerType();
    const filter = this.frameFilterType();

    if (journeyCriteria?.length > pageId) {
      journeyCriterias.push({
        origin: {
          airportTLA: getTLAFromAirport(journeyCriteria[pageId].origin)
        },
        destination: {
          airportTLA: getTLAFromAirport(journeyCriteria[pageId].destination),
          ...(regionId && { regionId })
        },
        ...(startDate &&
          endDate && {
            dateRange: {
              start: startDate,
              end: endDate
            }
          }),
        cabinClass,
        travelerDetail,
        filter,
        ...(tripType && { tripType })
      });
    }

    return journeyCriterias;
  }

  private frameTravelerType() {
    const travelerTypes = [];
    const travelerType = (this.searchCriteria?.travelerDetails as FlightsTravelerDetailsInput[]) || [];

    travelerType.forEach(elem => {
      const age = elem.ages ? elem.ages.map(age => age) : undefined;

      if (!age) {
        travelerTypes.push({
          type: elem.travelerType
        });
      }
      age?.forEach(age =>
        travelerTypes.push({
          type: elem.travelerType,
          age
        })
      );
    });

    return travelerTypes;
  }

  private getFilterTypes(filtersObjKey, filtersObj, filterValue) {
    if (filtersObjKey === 'numberOfStopsFilter') {
      const numOfStopValue = filtersObj[filtersObjKey];
      const filterNumberStopsValue = filterValue as FlightsStopInfoInput;
      filtersObj[filtersObjKey] = !numOfStopValue
        ? [filterNumberStopsValue?.numberOfStops]
        : [filterNumberStopsValue?.numberOfStops, ...filtersObj[filtersObjKey]];

      return;
    }
    const value = filtersObj[filtersObjKey];
    filtersObj[filtersObjKey] = !value ? [filterValue] : [filterValue, ...filtersObj[filtersObjKey]];
  }

  private frameFilterType() {
    const filters = [];
    const filtersObj = {};
    const filterStore = (this.searchCriteria?.searchFilterValuesList as FlightsSearchFilterValuesInput[]) || [];

    const keyIndex = {
      numOfStopFilterValue: 'numberOfStopsFilter',
      arrivalTimeFilterValue: 'arrivalTimeFilter',
      departureTimeFilterValue: 'departureTimeFilter',
      preferredAirlineFilterValue: 'airlinesFilter',
      seatChoiceBasedFilterValue: 'amenitiesFilter',
      carryOnBagBasedFilterValue: 'amenitiesFilter',
      noCancelFeeBasedFilterValue: 'amenitiesFilter',
      flexibleChangePolicyBasedFilterValue: 'amenitiesFilter'
    };

    filterStore.forEach(filter => {
      const keyForFilter = Object.keys(filter)[0];
      const keyForFilterValue = Object.keys(filter[keyForFilter])[0];
      const filterValue = filter[keyForFilter][keyForFilterValue];
      const filterObjKey = keyIndex[keyForFilter];

      this.getFilterTypes(filterObjKey, filtersObj, filterValue);
    });

    filters.push(filtersObj);

    return filters;
  }

  public prepareNavigation({ jcid, pageId, mipt }: FlightsBoundNavigationFragment): void {
    this.setJourneysContinuationId(jcid);
    this.pageId = pageId;
    this.multiItemPriceToken = mipt;
    this.cleanUpSortFilterOption();
    this.clearPagination();
  }

  public clearPageId(): void {
    this.pageId = '0';
  }

  public hardReload = () => {
    window.location.href = window.location.href;
  };

  public isSortedByRecommended() {
    return (
      this.searchCriteria?.sortOption?.sortType === FlightsSortType.BEST_BETS ||
      this.searchCriteria?.sortOption?.sortType === FlightsSortType.BEST_SORT
    );
  }

  private static stitchDate(date: DateInput): string {
    if (date.year && date.month && date.day) {
      return `${date.year}-${date.month}-${date.day}`;
    }

    return '';
  }

  public setServicingIntent(intent: string) {
    this.servicingIntent = intent || SearchCriteriaStore.DEFAULTS.servicingIntent;
    this.fetchFlexResults = this.shouldShowFlexSearch();
  }

  public shouldShowFlexSearch() {
    return this.servicingIntent != 'VOLUNTARY_CHANGE';
  }
}

SearchCriteriaStore.DEFAULTS = {
  searchCriteria: {
    flightsSearchContext: {
      tripType: FlightsTripType.ONE_WAY
    },
    journeyCriteria: [
      {
        departureDate: {
          day: 1,
          month: 1,
          year: 2020
        },
        destination: 'JFK',
        origin: 'SEA'
      }
    ],
    queryState: FlightsQueryState.LOADING,
    searchFilterValuesList: [],
    searchPreferences: {
      advancedFilters: [],
      airline: '',
      cabinClass: FlightsCabinClass.COACH
    },
    sortOption: null,
    travelerDetails: [
      {
        count: 1,
        travelerType: FlightsTravelerType.ADULT
      }
    ],
    searchPagination: null,
    flightsSearchComponentCriteria: {
      queryParams: []
    },
    shoppingContext: null
  },
  dialogSortAndFilter: {
    searchFilterValuesList: [],
    sortOption: null
  },
  dialogSideSheet: {
    searchFilterValuesList: [],
    addOnRequested: []
  },
  searchFormCriteria: {
    flightsSearchContext: {
      tripType: FlightsTripType.ONE_WAY
    },
    journeyCriteria: [
      {
        departureDate: {
          day: 1,
          month: 1,
          year: 2020
        },
        destination: 'JFK',
        origin: 'SEA'
      }
    ],
    queryState: FlightsQueryState.LOADING,
    searchPreferences: {
      advancedFilters: [],
      airline: '',
      cabinClass: FlightsCabinClass.COACH
    },
    travelerDetails: [
      {
        count: 1,
        travelerType: FlightsTravelerType.ADULT
      }
    ]
  },
  flexSearchCriteria: {
    flightsSearchContext: {
      tripType: FlightsTripType.ONE_WAY
    },
    journeyCriteria: [
      {
        departureDate: {
          day: 1,
          month: 1,
          year: 2020
        },
        destination: 'JFK',
        origin: 'SEA'
      }
    ],
    queryState: FlightsQueryState.LOADING,
    searchPreferences: {
      airline: '',
      cabinClass: FlightsCabinClass.COACH
    },
    travelerDetails: [
      {
        count: 1,
        travelerType: FlightsTravelerType.ADULT
      }
    ]
  },
  forceFetch: false,
  newSearch: true,
  fetchFlexResults: true,
  fetchSearchForm: true,
  reloadPageOnError: false,
  hasSmartShoppingCriteria: false,
  smartShoppingLoading: true,
  isMetaExperiment: false,
  sideSheetQueryContext: {
    addOnRequested: [],
    journeyContinuationId: null,
    isFirstLoad: true
  },
  servicingIntent: 'NONE'
};
